export default function ShreveportTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Shreveport Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Shreveport and Bossier City travelers actually move helps us anticipate the passport urgencies that arise here. From Barksdale AFB deployments to casino industry training to Texas business commutes, Northwest Louisiana travel operates on its own rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">SHV — Shreveport Regional Airport</h3>
                <p className="text-gray-400 text-xs">Southwest Shreveport, about 6 miles from downtown</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              SHV is the primary commercial airport for Northwest Louisiana, offering connections to Dallas, Houston, Atlanta, and Charlotte — the major international hubs for Shreveport travelers. Many residents also drive to Dallas (about 180 miles) for direct international flights to Europe, Asia, and Latin America. The airport serves a mix of Barksdale AFB personnel, casino industry employees, oil and gas workers, and business commuters to Texas.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking SHV connections through Dallas or Houston. Many countries require six months validity beyond your return date — and military travel orders often have non-negotiable departure windows.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-shield-star-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Barksdale AFB &amp; Military Travel</h3>
                <p className="text-gray-400 text-xs">Bossier City, Global Strike Command</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Barksdale Air Force Base is one of the largest military installations in the southern United States, home to Global Strike Command and the 2nd Bomb Wing. PCS orders often arrive with 30 days or less to report. Dependent passports must be current for overseas assignments. Deployment leave windows are fixed and unmovable. We regularly process expedited passports for Air Force families who received orders with short-fuse timelines — including infant passports for newborns before an overseas PCS.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Dependent passports must have at least six months validity for most overseas PCS assignments. Check every family member's expiration as soon as orders arrive — not the week before your flight from SHV or Dallas.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-vip-diamond-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Casino &amp; Gaming Industry Travel</h3>
                <p className="text-gray-400 text-xs">Bossier City casinos, Shreveport resorts</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Shreveport-Bossier City casino corridor is Louisiana's premier gaming destination, with several major resorts sending employees to international gaming conferences, training programs in Macau and Singapore, and vendor meetings in Europe. Casino industry travel often has fixed conference dates and compressed booking timelines — 3–4 weeks notice is common for international training and industry events. Many of these trips require both valid passports and sometimes business visas.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Gaming conferences in Asia (Macau, Singapore) often require specific business visas. Check your destination's visa requirements as soon as the conference is confirmed — some gaming industry visas require employer sponsorship letters.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-building-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Texas Business Commute &amp; Travel</h3>
                <p className="text-gray-400 text-xs">Dallas, Houston, Austin connections</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Many Shreveport residents work in Texas or commute to Dallas for business, using DFW International Airport for international travel. The Texas proximity creates a unique travel pattern where Shreveport residents often book international flights through Dallas with relatively short notice. This means they need current passports not just for the international destination, but for the domestic flight to Texas that connects to their international itinerary.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> If your international trip connects through Dallas, make sure your passport is valid for the entire journey — not just the final destination. Some countries require six months validity, and your passport must be valid for the return connection as well.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-oil-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Oil &amp; Gas Field Assignments</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              The Red River area and surrounding parishes host a significant oil and gas sector, with companies sending workers to West Africa, the Middle East, and Latin America for drilling and pipeline assignments. These field assignments often have fixed start dates with 2–4 weeks notice, and many require both valid passports and work visas for the host country.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}